import { useCallback, useEffect, useState } from 'react';

const PRESETS_CHANGED_EVENT = 'trinity:combined-export-presets-changed';

export interface CombinedExportPreset {
  id: string;
  name: string;
  selectedValues: string[];
  dateRange: {
    preset: string;
    from: string | null;
    to: string | null;
  };
  reportFilters: Record<string, Record<string, string>>;
}

const storageKeyFor = (churchId: string, userId: string): string =>
  `trinity.reports.combined-presets.${churchId}.${userId}`;

const readPresets = (storageKey: string | null): CombinedExportPreset[] => {
  if (!storageKey) return [];
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is CombinedExportPreset => {
      if (!item || typeof item !== 'object') return false;
      const preset = item as CombinedExportPreset;
      return (
        typeof preset.id === 'string' &&
        typeof preset.name === 'string' &&
        Array.isArray(preset.selectedValues) &&
        typeof preset.dateRange === 'object' &&
        typeof preset.reportFilters === 'object'
      );
    });
  } catch {
    return [];
  }
};

const writePresets = (storageKey: string | null, presets: CombinedExportPreset[]): void => {
  if (!storageKey) return;
  try {
    localStorage.setItem(storageKey, JSON.stringify(presets));
    queueMicrotask(() => {
      window.dispatchEvent(
        new CustomEvent(PRESETS_CHANGED_EVENT, { detail: { storageKey } }),
      );
    });
  } catch {
    // Ignore quota / private-mode failures.
  }
};

export const useCombinedExportPresets = (
  churchId: string | undefined,
  userId: string | undefined,
) => {
  const storageKey = churchId && userId ? storageKeyFor(churchId, userId) : null;
  const [presets, setPresets] = useState<CombinedExportPreset[]>(() => readPresets(storageKey));

  useEffect(() => {
    setPresets(readPresets(storageKey));
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey) return;
    const refresh = () => setPresets(readPresets(storageKey));
    const onStorage = (event: StorageEvent) => {
      if (event.key === storageKey) refresh();
    };
    const onChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ storageKey: string }>).detail;
      if (detail?.storageKey === storageKey) refresh();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(PRESETS_CHANGED_EVENT, onChanged);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(PRESETS_CHANGED_EVENT, onChanged);
    };
  }, [storageKey]);

  const savePreset = useCallback(
    (preset: CombinedExportPreset) => {
      setPresets((previous) => {
        const withoutDuplicate = previous.filter((item) => item.id !== preset.id);
        const updated = [...withoutDuplicate, preset].sort((left, right) =>
          left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }),
        );
        writePresets(storageKey, updated);
        return updated;
      });
    },
    [storageKey],
  );

  const deletePreset = useCallback(
    (id: string) => {
      setPresets((previous) => {
        const updated = previous.filter((item) => item.id !== id);
        writePresets(storageKey, updated);
        return updated;
      });
    },
    [storageKey],
  );

  return { presets, savePreset, deletePreset };
};
